import { ParameterField } from '../components/sections/DynamicParametersSection';

export interface ScriptOption {
  value: string;
  label: string;
  description: string;
  impact: string;
}

export interface ScriptConfig {
  name: string;
  description: string;
  impact: string;
  parameters: ParameterField[];
  supportsMultiRow?: boolean;
}

export type ScriptConfigMap = Record<string, ScriptConfig>;

export const SCRIPT_CONFIGS: ScriptConfigMap = {
  'update-paid-invoice': {
    name: 'Update Paid Total & Invoice Status',
    description:
      'Script fix to allow updating PaidTotal and InvoiceStatus against a AcctInvoiceId in Infinity',
    impact: 'Invoice Status Change | Low Impact',
    supportsMultiRow: false,
    parameters: [
      { name: 'policyId', label: 'PolicyId', type: 'text', required: true },
      { name: 'acctInvoiceId', label: 'AcctInvoiceId', type: 'text', required: true },
      { name: 'paidTotal', label: 'Paid Total', type: 'number', required: true },
      { name: 'invoiceStatus', label: 'InvoiceStatus', type: 'text', required: true },
      { name: 'acctInvoiceActivityId', label: 'AcctInvoiceActivityId', type: 'text', required: true },
      { name: 'acctPayableId', label: 'AcctPayableId', type: 'text', required: true },
      { name: 'acctMasterId', label: 'AcctMasterId', type: 'text', required: true },
      { name: 'payableSourceCode', label: 'PayableSourceCode', type: 'text', required: true },
      { name: 'acctPayableActivityId', label: 'AcctPayableActivityId', type: 'text', required: true },
    ],
  },
  'update-deleted-claims': {
    name: 'Update Deleted Claims Coverages',
    description: 'Script fix to allow updating deleted claims coverages in Infinity',
    impact: 'Claims Coverage Update | Low Impact',
    supportsMultiRow: true,
    parameters: [
      {
        name: 'claimcovId',
        label: 'claimcovId',
        type: 'text',
        placeholder: 'Enter claim coverage ID',
        required: true,
      },
      {
        name: 'claimid',
        label: 'claimid',
        type: 'text',
        placeholder: 'Enter claim ID',
        required: true,
      },
    ],
  },
  'update-claims-number': {
    name: 'Update Claims Number',
    description: 'Script fix to allow updating claims Number',
    impact: 'Claims Number Update | Low Impact',
    supportsMultiRow: true,
    parameters: [
      {
        name: 'claimNumberFrom',
        label: 'Claim Number From',
        type: 'text',
        placeholder: 'Enter current claim number',
        required: true,
      },
      {
        name: 'claimNumberTo',
        label: 'Claim Number To',
        type: 'text',
        placeholder: 'Enter new claim number',
        required: true,
      },
    ],
  },
  'update-claims-coverage-limits': {
    name: 'Update Claims Coverage Limits',
    description: 'Script fix to allow updating Claims Coverage Limits',
    impact: 'Claims Coverage Limits Update | Low Impact',
    supportsMultiRow: true,
    parameters: [
      {
        name: 'claimCovLimitId',
        label: 'ClaimCovLimitId',
        type: 'text',
        placeholder: 'Enter claim coverage limit ID',
        required: true,
      },
      {
        name: 'limitValue',
        label: 'LimitValue',
        type: 'text',
        placeholder: 'Enter limit value',
        required: true,
      },
    ],
  },
  'update-uw-limits': {
    name: 'Update UW Limits',
    description: 'Script fix to allow updating UW Limits',
    impact: 'UW Limits Update | Low Impact',
    supportsMultiRow: true,
    parameters: [
      {
        name: 'uwLimitId',
        label: 'UWLimitId',
        type: 'text',
        placeholder: 'Enter UW limit ID',
        required: true,
      },
      {
        name: 'limitValue',
        label: 'LimitValue',
        type: 'text',
        placeholder: 'Enter limit value',
        required: true,
      },
    ],
  },
  'update-parent-agency-kraft-lake': {
    name: 'Update Parent Agency Kraft Lake',
    description: "Script Fix to Update Agent's Parent Contact as Kraft Insurance Agency",
    impact: 'Parent Agency Kraft Lake Update | Low Impact',
    supportsMultiRow: true,
    parameters: [
      {
        name: 'contactId',
        label: 'Contact Id',
        type: 'text',
        placeholder: 'Enter contact ID',
        required: true,
      },
    ],
  },
   'insert-underwriter': {
    name: 'Add UW in Payment Portal',
    description: 'Script fix to allow add underwriter in payment portal',
    impact: 'Add UW in Payment Portal | Low Impact',
    supportsMultiRow: true,
    parameters: [
      {
        name: 'userEmail',
        label: 'Email Id',
        type: 'email',
        placeholder: 'Enter user email',
        required: true,
      },
      {
        name: 'subCompanyCode',
        label: 'Sub-Company Code',
        type: 'text',
        placeholder: 'Enter sub company code',
        required: true,
      },
    ],
  },
};

export const SCRIPT_OPTIONS: ScriptOption[] = Object.entries(SCRIPT_CONFIGS).map(
  ([key, config]) => ({
    value: key,
    label: config.name,
    description: config.description,
    impact: config.impact,
  })
);

export interface ClaimsScriptConfig {
  value: string;
  label: string;
  description: string;
  impact: string;
  parameters: ParameterField[];
}

export const CLAIMS_SCRIPT_KEYS = [
  'update-deleted-claims',
  'update-claims-number',
  'update-claims-coverage-limits',
  'update-uw-limits',
  'update-parent-agency-kraft-lake',
] as const;

export const CLAIMS_SCRIPT_OPTIONS: ClaimsScriptConfig[] = CLAIMS_SCRIPT_KEYS.map((key) => {
  const script = SCRIPT_CONFIGS[key];
  return {
    value: key,
    label: script.name,
    description: script.description,
    impact: script.impact,
    parameters: script.parameters,
  };
});
